'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Plus, MoreHorizontal, MessageSquare, Calendar } from 'lucide-react';
import { PROJECTS, TEAM_MEMBERS, type Task, type TaskStatus } from '@/lib/mockData';
import PriorityBadge from '@/components/ui/PriorityBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import AvatarGroup from '@/components/ui/AvatarGroup';
import ProgressBar from '@/components/ui/ProgressBar';
import AddTaskModal from './AddTaskModal';
import { toast } from 'sonner';

const COLUMNS: TaskStatus[] = ['To Do', 'In Progress', 'In Review', 'Done'];

const COLUMN_STYLES: Record<TaskStatus, { header: string; dot: string }> = {
  'To Do': { header: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
  'In Progress': { header: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  'In Review': { header: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  Done: { header: 'bg-green-50 text-green-700', dot: 'bg-green-500' },
};

export default function ProjectDetailContent() {
  const project = PROJECTS[0]; // Website Redesign — default project for detail view
  const [tasks, setTasks] = useState<Task[]>(project.tasks);
  const [addTaskColumn, setAddTaskColumn] = useState<TaskStatus | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<TaskStatus | null>(null);

  const tasksByColumn = (col: TaskStatus) => tasks.filter((t) => t.status === col);
  const doneTasks = tasks.filter((t) => t.status === 'Done').length;
  const progress = tasks.length > 0 ? Math.round((doneTasks / tasks.length) * 100) : 0;

  const handleDragStart = (taskId: string) => setDragging(taskId);
  const handleDragEnd = () => { setDragging(null); setDragOver(null); };

  const handleDrop = (col: TaskStatus) => {
    if (!dragging) return;
    setTasks((prev) =>
      prev.map((t) => (t.id === dragging ? { ...t, status: col } : t))
    );
    toast.success(`Task moved to ${col}`);
    setDragging(null);
    setDragOver(null);
  };

  const handleAddTask = (title: string, priority: Task['priority'], assigneeId: string, dueDate: string) => {
    // Backend integration point: POST /api/tasks
    const newTask: Task = {
      id: `task-new-${Date.now()}`,
      title,
      description: '',
      status: addTaskColumn!,
      priority,
      assigneeId,
      projectId: project.id,
      dueDate,
      tags: [],
      subTasks: [],
      comments: [],
      attachments: [],
      commentCount: 0,
      createdById: 'user-001',
      createdAt: new Date().toISOString(),
    };
    setTasks((prev) => [...prev, newTask]);
    setAddTaskColumn(null);
    toast.success('Task added to board');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Project header */}
      <div className="bg-card border-b border-border px-6 py-4 flex-shrink-0">
        <Link
          href="/projects-page"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors mb-3"
        >
          <ArrowLeft size={13} /> Back to Projects
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-xl font-semibold text-foreground">{project.name}</h1>
              <StatusBadge status={project.status} />
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1">{project.description}</p>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <AvatarGroup memberIds={project.teamMemberIds} max={4} size="md" />
            <div className="hidden sm:block w-32">
              <ProgressBar value={progress} showLabel size="md" />
              <p className="text-xs text-muted-foreground mt-1">{doneTasks}/{tasks.length} tasks done</p>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban board */}
      <div className="flex-1 overflow-x-auto p-6 scrollbar-thin">
        <div className="flex gap-4 min-w-max h-full">
          {COLUMNS.map((col) => {
            const colTasks = tasksByColumn(col);
            const styles = COLUMN_STYLES[col];
            const isOver = dragOver === col;

            return (
              <div
                key={`col-${col}`}
                className="flex flex-col w-72 flex-shrink-0"
                onDragOver={(e) => { e.preventDefault(); setDragOver(col); }}
                onDrop={() => handleDrop(col)}
                onDragLeave={() => setDragOver(null)}
              >
                {/* Column header */}
                <div className={`flex items-center justify-between px-3 py-2 rounded-lg mb-3 ${styles.header}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${styles.dot}`} />
                    <span className="text-sm font-semibold">{col}</span>
                    <span className="text-xs font-medium opacity-70 bg-white/50 px-1.5 py-0.5 rounded-full">
                      {colTasks.length}
                    </span>
                  </div>
                  <button className="p-1 rounded hover:bg-white/50 transition-colors">
                    <MoreHorizontal size={14} />
                  </button>
                </div>

                {/* Task cards */}
                <div
                  className={`flex-1 space-y-2.5 kanban-column rounded-xl p-2 transition-colors duration-200 ${
                    isOver ? 'bg-primary/5 ring-2 ring-primary/20' : 'bg-transparent'
                  }`}
                >
                  {colTasks.map((task) => {
                    const assignee = TEAM_MEMBERS.find((m) => m.id === task.assigneeId);
                    const isOverdue = task.dueDate < '2026-05-27' && task.status !== 'Done';

                    return (
                      <div
                        key={`kanban-${task.id}`}
                        draggable
                        onDragStart={() => handleDragStart(task.id)}
                        onDragEnd={handleDragEnd}
                        className={`bg-card border rounded-xl p-3.5 task-card-hover cursor-grab active:cursor-grabbing select-none ${
                          dragging === task.id
                            ? 'opacity-50 border-primary/40' :'border-border hover:border-primary/20'
                        }`}
                      >
                        {/* Tags */}
                        {task.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {task.tags.slice(0, 2).map((tag) => (
                              <span
                                key={`tag-${task.id}-${tag}`}
                                className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Title */}
                        <Link href="/task-detail">
                          <p className="text-sm font-medium text-foreground leading-snug hover:text-primary transition-colors mb-2.5">
                            {task.title}
                          </p>
                        </Link>

                        {/* Priority */}
                        <div className="mb-3">
                          <PriorityBadge priority={task.priority} size="sm" />
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {assignee && (
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                                style={{ backgroundColor: assignee.avatarColor }}
                                title={assignee.name}
                              >
                                {assignee.avatarInitials}
                              </div>
                            )}
                            {task.commentCount > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <MessageSquare size={11} />
                                <span>{task.commentCount}</span>
                              </div>
                            )}
                          </div>

                          <div
                            className={`flex items-center gap-1 text-xs font-medium ${
                              isOverdue ? 'text-red-500' : 'text-muted-foreground'
                            }`}
                          >
                            <Calendar size={11} />
                            <span>{task.dueDate.slice(5)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {colTasks.length === 0 && !isOver && (
                    <div className="flex items-center justify-center h-24 border-2 border-dashed border-border rounded-xl">
                      <p className="text-xs text-muted-foreground">Drop tasks here</p>
                    </div>
                  )}
                </div>

                {/* Add task button */}
                <button
                  onClick={() => setAddTaskColumn(col)}
                  className="mt-2.5 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 w-full"
                >
                  <Plus size={14} />
                  Add task
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {addTaskColumn && (
        <AddTaskModal
          column={addTaskColumn}
          onClose={() => setAddTaskColumn(null)}
          onAdd={handleAddTask}
        />
      )}
    </div>
  );
}